/**
 * PeriodClock — large countdown display + start/pause/end controls.
 *
 * Pulled from the live mode timer block in EstadisticasNew.js, restyled
 * with the v2 theme. The hook of truth is useGameClock — this component
 * is purely presentational.
 */

import React from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import StopIcon from '@mui/icons-material/Stop';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useStatsTheme, withAlpha } from '../theme/statsTheme';
import { formatTime } from '../../utils/timeUtils';

export default function PeriodClock({
  currentPeriod,
  numberOfPeriods = 4,
  timeLeft,
  isRunning,
  periodStarted,
  onStart,
  onPause,
  onEnd,
  t,
}) {
  const theme = useStatsTheme();
  const danger = timeLeft < 60;

  const tt = (key, fallback) => (typeof t === 'function' ? t(`estadisticasNew.${key}`, { defaultValue: fallback }) : fallback);

  const isLastPeriod = currentPeriod >= numberOfPeriods;

  return (
    <Box
      role="timer"
      aria-label={tt('periodClock', 'Period clock')}
      sx={{
        backgroundColor: theme.surfaces.elevated,
        borderRadius: `${theme.radius.lg}px`,
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1.5,
        boxShadow: theme.shadows.md,
        border: `1px solid ${theme.surfaces.border}`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.2,
          py: 0.4,
          borderRadius: theme.radius.pill,
          backgroundColor: theme.accentSoft,
          border: `1px solid ${theme.accentBorder}`,
        }}
      >
        <AccessTimeIcon sx={{ fontSize: 14, color: theme.accent }} />
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: theme.text.primary, letterSpacing: '0.06em' }}>
          P{currentPeriod} / {numberOfPeriods}
        </Typography>
      </Box>

      <Typography
        sx={{
          fontFamily: theme.typography.fontFamily,
          fontSize: 72,
          fontWeight: 800,
          letterSpacing: '-0.03em',
          color: danger ? theme.actionColors.missed : theme.text.primary,
          lineHeight: 1,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {formatTime(Math.max(0, timeLeft || 0))}
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
        {!isRunning ? (
          <Tooltip title={periodStarted ? tt('resume', 'Resume') : tt('start', 'Start')}>
            <IconButton
              onClick={onStart}
              aria-label={periodStarted ? 'resume' : 'start'}
              sx={{
                width: 56,
                height: 56,
                backgroundColor: withAlpha(theme.actionColors.made, 0.22),
                border: `1px solid ${withAlpha(theme.actionColors.made, 0.55)}`,
                color: theme.actionColors.made,
                '&:hover': { backgroundColor: withAlpha(theme.actionColors.made, 0.34) },
              }}
            >
              <PlayArrowIcon sx={{ fontSize: 28 }} />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title={tt('pause', 'Pause')}>
            <IconButton
              onClick={onPause}
              aria-label="pause"
              sx={{
                width: 56,
                height: 56,
                backgroundColor: withAlpha(theme.actionColors.warning, 0.22),
                border: `1px solid ${withAlpha(theme.actionColors.warning, 0.55)}`,
                color: theme.actionColors.warning,
                '&:hover': { backgroundColor: withAlpha(theme.actionColors.warning, 0.34) },
              }}
            >
              <PauseIcon sx={{ fontSize: 26 }} />
            </IconButton>
          </Tooltip>
        )}

        {periodStarted && !isLastPeriod && (
          <Tooltip title={tt('endPeriodShort', 'End period')}>
            <IconButton
              onClick={onEnd}
              aria-label="end period"
              sx={{
                width: 44,
                height: 44,
                backgroundColor: withAlpha(theme.text.muted, 0.16),
                border: `1px solid ${theme.surfaces.border}`,
                color: theme.text.secondary,
                '&:hover': { backgroundColor: withAlpha(theme.text.muted, 0.28) },
              }}
            >
              <StopIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}
